import type { ReactNode } from 'react';
import { type ToastClassnames, toast as sonnerToast } from 'sonner';

import { type ToastVariant, resolveToastId } from './toast.util';

type ToastActionElement = {
  label: ReactNode;
  onClick: () => void;
};

type ToastInput = {
  action?: ToastActionElement;
  className?: string;
  classNames?: ToastClassnames;
  description?: ReactNode;
  duration?: number;
  title?: ReactNode;
  variant?: ToastVariant;
};

const TOAST_CLOSE_BUTTON_CLASS_NAME = '!left-auto !right-0 !top-3 !translate-x-0 !translate-y-0';

function resolveMessage(params: { description?: ReactNode; title?: ReactNode }): ReactNode {
  const { description, title } = params;
  return title ?? description ?? '';
}

function resolveOptions(params: ToastInput) {
  const { action, className, classNames, description, duration, title } = params;
  const resolvedClassNames = {
    ...classNames,
    closeButton: classNames?.closeButton ?? TOAST_CLOSE_BUTTON_CLASS_NAME,
  };

  return {
    action,
    className,
    classNames: resolvedClassNames,
    description: title ? description : undefined,
    duration,
  };
}

function showToast(params: ToastInput): void {
  const { variant = 'default' } = params;
  const message = resolveMessage({ description: params.description, title: params.title });
  const options = {
    ...resolveOptions(params),
    id: resolveToastId({
      description: params.description,
      title: params.title,
      variant,
    }),
  };

  if (variant === 'destructive') {
    sonnerToast.error(message, options);
    return;
  }

  if (variant === 'success') {
    sonnerToast.success(message, options);
    return;
  }

  sonnerToast(message, options);
}

function successToast(params: Omit<ToastInput, 'variant'>) {
  showToast({
    ...params,
    variant: 'success',
    className: 'min-h-16!',
    classNames: {
      title: 'font-normal! text-sm! font-sans!',
    },
  });
}

function errorToast(params: Omit<ToastInput, 'variant'>) {
  showToast({
    ...params,
    variant: 'destructive',
    duration: params.duration,
    className: 'border! border-red-200! min-h-16! bg-red-50! text-red-600! font-normal! text-sm! font-sans!',
    classNames: {
      title: 'font-normal! text-sm! font-sans!',
    },
  });
}

export { successToast, errorToast };
